import type { QuestionnaireResponse } from "@medplum/fhirtypes";

import { isValid } from "date-fns";

import {
  isOtherDisplaySelected,
  OTHER_DISPLAY,
  readYesNoOtherDisplayFromAnswer,
  yesNoOtherDisplayToCoding,
} from "@/components/ClearableYesNoRadioField";
import {
  BIRTHING_BODY_DATE_TIME_LINK_ID,
  BIRTHING_CROWN_DATE_TIME_LINK_ID,
  BIRTHING_DATE_LINK_ID,
  BIRTHING_DATE_TIME_LINK_ID,
  BIRTHING_DELIVERY_METHOD_LINK_ID,
  BIRTHING_DELIVERY_METHOD_OTHER_LINK_ID,
  BIRTHING_FETAL_PRESENTATION_LINK_ID,
  BIRTHING_HEAD_DATE_TIME_LINK_ID,
  BIRTHING_IN_WATER_LINK_ID,
  BIRTHING_IN_WATER_OTHER_LINK_ID,
  BIRTHING_NOTES_LINK_ID,
  BIRTHING_POSITION_LINK_ID,
  BIRTHING_ROOT_LINK_ID,
  BIRTHING_SIGNATURE_INITIALS_LINK_ID,
  BIRTHING_TIME_LINK_ID,
  BIRTHING_TYPE_LINK_ID,
} from "@/encounters/components/EncounterLabor/flowsheet-forms/birthing/constants/birthing-link-ids";
import type { BirthingFlowsheetFormValues } from "@/encounters/components/EncounterLabor/flowsheet-forms/birthing/types/birthing-form";
import { getEmptyBirthingFlowsheetFormValues } from "@/encounters/components/EncounterLabor/flowsheet-forms/birthing/utils/birthing-form-defaults";
import {
  parseCodingFromNativeSelect,
  readCodingAnswerAsNativeSelectValue,
} from "@/encounters/components/EncounterLabor/flowsheet-forms/shared/labor-flowsheet-coding-select";
import {
  findQuestionnaireResponseItem,
  readQuestionnaireDateTimeAnswer,
  readQuestionnaireLocalDateAnswer,
  readQuestionnaireSignatureInitials,
  readQuestionnaireStringAnswer,
} from "@/encounters/components/EncounterLabor/flowsheet-forms/shared/questionnaire-response-readers";
import { BirthingComponent } from "@/fhir/enums/quilted-health";
import { Systems } from "@/fhir/enums/systems";

const BIRTHING_IN_WATER_OTHER_CODING = {
  system: Systems.QH,
  code: BirthingComponent.ClientInWaterOther,
  display: OTHER_DISPLAY,
};

/**
 * Detects the delivery-method "Other" selection by coding identity (system + code) rather than
 * display text, since a coding read back from a previously-saved QuestionnaireResponse may have
 * been written without a `display` (display is optional on a FHIR Coding).
 */
export function isBirthingDeliveryMethodOtherSelected(value: string | null | undefined): boolean {
  const coding = parseCodingFromNativeSelect(value);
  return coding?.system === Systems.QH && coding?.code === BirthingComponent.DeliveryMethodOther;
}

/**
 * Formats a `dateTime`-typed field's value as a UTC instant (matching how the rest of this app
 * normalizes labor flowsheet dateTime values), or clears it when unset or invalid.
 */
function toIsoDateTimeOrNull(value: Date | null): string | null {
  return value && isValid(value) ? value.toISOString() : null;
}

export function mapQuestionnaireResponseToBirthingFormValues(
  questionnaireResponse: QuestionnaireResponse | undefined,
): BirthingFlowsheetFormValues {
  const emptyValues = getEmptyBirthingFlowsheetFormValues();
  const root = findQuestionnaireResponseItem(questionnaireResponse?.item, BIRTHING_ROOT_LINK_ID);
  if (!root?.item) {
    return emptyValues;
  }

  const dateTimeGroup = findQuestionnaireResponseItem(root.item, BIRTHING_DATE_TIME_LINK_ID);
  const timeAnswer = findQuestionnaireResponseItem(dateTimeGroup?.item, BIRTHING_TIME_LINK_ID)
    ?.answer?.[0]?.valueTime;

  const inWaterItem = findQuestionnaireResponseItem(root.item, BIRTHING_IN_WATER_LINK_ID);

  return {
    ...emptyValues,
    birthingDate: readQuestionnaireLocalDateAnswer(
      findQuestionnaireResponseItem(dateTimeGroup?.item, BIRTHING_DATE_LINK_ID),
    ),
    birthingTime: timeAnswer ?? null,
    birthingCrownDateTime: readQuestionnaireDateTimeAnswer(
      findQuestionnaireResponseItem(root.item, BIRTHING_CROWN_DATE_TIME_LINK_ID),
    ),
    birthingHeadDateTime: readQuestionnaireDateTimeAnswer(
      findQuestionnaireResponseItem(root.item, BIRTHING_HEAD_DATE_TIME_LINK_ID),
    ),
    birthingBodyDateTime: readQuestionnaireDateTimeAnswer(
      findQuestionnaireResponseItem(root.item, BIRTHING_BODY_DATE_TIME_LINK_ID),
    ),
    birthingType: readCodingAnswerAsNativeSelectValue(
      findQuestionnaireResponseItem(root.item, BIRTHING_TYPE_LINK_ID),
    ),
    birthingPosition: readCodingAnswerAsNativeSelectValue(
      findQuestionnaireResponseItem(root.item, BIRTHING_POSITION_LINK_ID),
    ),
    birthingInWater: readYesNoOtherDisplayFromAnswer(
      inWaterItem?.answer?.[0],
      BIRTHING_IN_WATER_OTHER_CODING,
    ),
    birthingInWaterOther: readQuestionnaireStringAnswer(
      findQuestionnaireResponseItem(root.item, BIRTHING_IN_WATER_OTHER_LINK_ID),
    ),
    birthingFetalPresentation: readCodingAnswerAsNativeSelectValue(
      findQuestionnaireResponseItem(root.item, BIRTHING_FETAL_PRESENTATION_LINK_ID),
    ),
    birthingDeliveryMethod: readCodingAnswerAsNativeSelectValue(
      findQuestionnaireResponseItem(root.item, BIRTHING_DELIVERY_METHOD_LINK_ID),
    ),
    birthingDeliveryMethodOther: readQuestionnaireStringAnswer(
      findQuestionnaireResponseItem(root.item, BIRTHING_DELIVERY_METHOD_OTHER_LINK_ID),
    ),
    birthingNotes: readQuestionnaireStringAnswer(
      findQuestionnaireResponseItem(root.item, BIRTHING_NOTES_LINK_ID),
    ),
    birthingSignatureInitials: readQuestionnaireSignatureInitials(
      findQuestionnaireResponseItem(root.item, BIRTHING_SIGNATURE_INITIALS_LINK_ID),
    ),
  };
}

export function mapBirthingFormValuesToQuestionnaireValues(
  formValues: BirthingFlowsheetFormValues,
): Record<string, unknown> {
  return {
    [BIRTHING_ROOT_LINK_ID]: {
      [BIRTHING_DATE_TIME_LINK_ID]: {
        [BIRTHING_DATE_LINK_ID]: formValues.birthingDate,
        [BIRTHING_TIME_LINK_ID]: formValues.birthingTime,
      },
      [BIRTHING_CROWN_DATE_TIME_LINK_ID]: toIsoDateTimeOrNull(formValues.birthingCrownDateTime),
      [BIRTHING_HEAD_DATE_TIME_LINK_ID]: toIsoDateTimeOrNull(formValues.birthingHeadDateTime),
      [BIRTHING_BODY_DATE_TIME_LINK_ID]: toIsoDateTimeOrNull(formValues.birthingBodyDateTime),
      [BIRTHING_TYPE_LINK_ID]: parseCodingFromNativeSelect(formValues.birthingType),
      [BIRTHING_POSITION_LINK_ID]: parseCodingFromNativeSelect(formValues.birthingPosition),
      [BIRTHING_IN_WATER_LINK_ID]: yesNoOtherDisplayToCoding(
        formValues.birthingInWater,
        BIRTHING_IN_WATER_OTHER_CODING,
      ),
      [BIRTHING_IN_WATER_OTHER_LINK_ID]: isOtherDisplaySelected(formValues.birthingInWater)
        ? formValues.birthingInWaterOther
        : null,
      [BIRTHING_FETAL_PRESENTATION_LINK_ID]: parseCodingFromNativeSelect(
        formValues.birthingFetalPresentation,
      ),
      [BIRTHING_DELIVERY_METHOD_LINK_ID]: parseCodingFromNativeSelect(
        formValues.birthingDeliveryMethod,
      ),
      [BIRTHING_DELIVERY_METHOD_OTHER_LINK_ID]: isBirthingDeliveryMethodOtherSelected(
        formValues.birthingDeliveryMethod,
      )
        ? formValues.birthingDeliveryMethodOther
        : null,
      [BIRTHING_NOTES_LINK_ID]: formValues.birthingNotes,
      [BIRTHING_SIGNATURE_INITIALS_LINK_ID]: formValues.birthingSignatureInitials,
    },
  };
}
